import { useMemo } from 'react'
import { Filters } from '@/service-library/request-types'
import { useGetLiveListConfigs } from '@/services/hooks/relay/dynamic'
import { Document, DocumentRow, DocumentRowValue } from '@/types/documents'
import { StoredFilter } from '@/types/live-list-filter'
import { createFilterBuilder } from '@/utils/live-list-filter-builder'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'

export type UseLiveListFilterBuilderOptions = {
  storedFilter: StoredFilter | null
  infoTypeId: string | null | undefined
  document?: Document
  documentRow?: DocumentRow
  /**
   * Optional resolver from DocumentRowValuesContext. When provided, the hook
   * uses it to resolve row field values instead of reading from
   * documentRow.document_row_values (which may not be populated).
   */
  getFieldDocumentRowValue?: (
    fieldId: string,
    rowId: string | null,
  ) => DocumentRowValue | undefined
  /**
   * Extra params merged into the built filter (org_id, query, etc.).
   * Callers should memoize this object to avoid unnecessary recomputation.
   */
  extra: Filters | null
}

export type UseLiveListFilterBuilderResult = {
  liveListUrl: string | null
  domainFilters: Filters | null
}

/**
 * Builds live-list filter params from configuration.
 * Internally retrieves LiveListConfigs and derives projectGridFieldIdByInfoType
 * from the project context, so callers don't need to supply those.
 *
 * Returns `{ liveListUrl: null, domainFilters: null }` when configs aren't
 * loaded yet or when the builder throws (e.g. required data not ready).
 */
export default function useLiveListFilterBuilder({
  storedFilter,
  infoTypeId,
  document,
  documentRow,
  getFieldDocumentRowValue,
  extra,
}: UseLiveListFilterBuilderOptions): UseLiveListFilterBuilderResult {
  const { fieldsMap } = useProjectContext()
  const { liveListConfigs } = useGetLiveListConfigs()

  const projectGridFieldIdByInfoType = useMemo(
    () =>
      Object.fromEntries(
        Object.values(fieldsMap)
          .filter((f) => f.info_type_id)
          .map((f) => [f.info_type_id as string, f.id]),
      ),
    [fieldsMap],
  )

  const resolveRowValue = useMemo(() => {
    if (!getFieldDocumentRowValue) return undefined
    return (row: DocumentRow, rowInfoTypeId: string) => {
      const fieldId = projectGridFieldIdByInfoType[rowInfoTypeId]
      if (!fieldId) return undefined
      return getFieldDocumentRowValue(fieldId, row.id)
    }
  }, [getFieldDocumentRowValue, projectGridFieldIdByInfoType])

  return useMemo(() => {
    if (!liveListConfigs || !infoTypeId) {
      return { liveListUrl: null, domainFilters: null }
    }
    let builder: ReturnType<typeof createFilterBuilder> | null = null
    try {
      builder = createFilterBuilder({
        storedFilter,
        infoTypeId,
        liveListConfigs,
        document,
        documentRow,
        projectGridFieldIdByInfoType,
        resolveRowValue,
        extra,
      })
      return {
        liveListUrl: builder.liveListUrl,
        domainFilters: builder.build(),
      }
    } catch (error) {
      // TODO: handle these errors better in the future
      // eslint-disable-next-line no-console
      console.warn(
        'Live list filter builder threw an error. ' +
          'This may be expected if required data is not ready yet.',
        error,
      )
      if (builder) {
        return {
          liveListUrl: builder.liveListUrl,
          domainFilters: extra,
        }
      }
      return { liveListUrl: null, domainFilters: null }
    }
  }, [
    liveListConfigs,
    storedFilter,
    infoTypeId,
    document,
    documentRow,
    projectGridFieldIdByInfoType,
    resolveRowValue,
    extra,
  ])
}
